import { useEffect, useRef, useState } from "react";
import type { Call } from "@shared/modules/x-appels";
import { listCalls } from "./api";

/**
 * Appels en cours, rafraîchis toutes les 2,5 s tant que l'onglet est visible.
 * La téléphonie pousse la transcription au fil de l'eau : un sondage court
 * suffit pour la voir défiler, sans connexion persistante à maintenir.
 * `onEnded` prévient quand un appel quitte la liste (pour recharger l'historique).
 */
export function useLiveCalls(onEnded?: () => void): { live: Call[]; failed: boolean } {
  const [live, setLive] = useState<Call[]>([]);
  const [failed, setFailed] = useState(false);
  const known = useRef<Set<string>>(new Set());
  const callback = useRef(onEnded);
  callback.current = onEnded;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      if (document.visibilityState === "visible") {
        try {
          const rows = await listCalls({ status: ["en_cours"], limit: 20 });
          if (cancelled) return;
          const ids = new Set(rows.map((r) => r.id));
          const ended = [...known.current].some((id) => !ids.has(id));
          known.current = ids;
          setLive(rows);
          setFailed(false);
          if (ended) callback.current?.();
        } catch {
          if (!cancelled) setFailed(true);
        }
      }
      if (!cancelled) timer = setTimeout(tick, 2500);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return { live, failed };
}

/** Secondes écoulées depuis `startedAt`, mises à jour chaque seconde. */
export function useElapsed(startedAt: string | number): number {
  const compute = () =>
    Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 1000));
  const [elapsed, setElapsed] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setElapsed(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedAt]);
  return elapsed;
}
